import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Star, Send, Loader2, MessageSquare, CheckCircle, Clock, 
  ThumbsUp, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StarRating({ rating, onRate, size = 'lg', interactive = true }) {
  const [hover, setHover] = useState(0);
  const starSize = size === 'lg' ? 'w-8 h-8' : size === 'md' ? 'w-6 h-6' : 'w-4 h-4';
  return (
    <div className="flex gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRate?.(s)}
          onMouseEnter={() => interactive && setHover(s)}
          onMouseLeave={() => interactive && setHover(0)}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
          data-testid={`star-${s}`}
        >
          <Star
            className={`${starSize} transition-colors ${
              s <= (hover || rating)
                ? 'fill-amber-400 text-amber-400'
                : 'fill-gray-200 text-gray-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function ReviewsPage() {
  const { api } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    rating: 0, title: '', comment: '', course: '', reviewType: 'general',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [reviewsRes, coursesRes] = await Promise.all([
        api.get('/reviews/'),
        fetch(`${API_URL}/api/courses/?page_size=200`).then(r => r.json()),
      ]);
      setReviews(Array.isArray(reviewsRes.data) ? reviewsRes.data : reviewsRes.data.results || []);
      const courseList = coursesRes.results || coursesRes || [];
      setCourses(courseList);
    } catch (e) {
      console.error('Error fetching reviews:', e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.rating === 0) { toast.error('Please select a star rating'); return; }
    if (!form.comment.trim()) { toast.error('Please write a review'); return; }
    setSubmitting(true);
    try {
      const payload = {
        rating: form.rating,
        title: form.title,
        comment: form.comment,
      };
      if (form.reviewType === 'course' && form.course) {
        payload.course = form.course;
      }
      await api.post('/reviews/', payload);
      toast.success('Review submitted! It will appear after admin approval.');
      setForm({ rating: 0, title: '', comment: '', course: '', reviewType: 'general' });
      setShowForm(false);
      fetchData();
    } catch (e) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="reviews-loading">
        <Loader2 className="h-8 w-8 animate-spin text-rsc-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reviews-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-rsc-navy" />
            Reviews
          </h1>
          <p className="text-sm text-gray-500 mt-1">Share your experience with Ready Set STEM</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-rsc-navy text-white hover:bg-rsc-navy/90"
          data-testid="write-review-btn"
        >
          <Star className="h-4 w-4 mr-2" /> Write a Review
        </Button>
      </div>

      {/* Review Form */}
      {showForm && (
        <Card className="border-2 border-rsc-navy/20" data-testid="review-form">
          <CardContent className="p-6">
            <h3 className="font-semibold text-lg mb-4">Write Your Review</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Review Type */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={form.reviewType === 'general' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setForm(p => ({ ...p, reviewType: 'general', course: '' }))}
                  className={form.reviewType === 'general' ? 'bg-rsc-navy' : ''}
                  data-testid="review-type-general"
                >
                  <ThumbsUp className="h-3.5 w-3.5 mr-1.5" /> General Review
                </Button>
                <Button
                  type="button"
                  variant={form.reviewType === 'course' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setForm(p => ({ ...p, reviewType: 'course' }))}
                  className={form.reviewType === 'course' ? 'bg-rsc-navy' : ''}
                  data-testid="review-type-course"
                >
                  <BookOpen className="h-3.5 w-3.5 mr-1.5" /> Course Review
                </Button>
              </div>

              {/* Course Selector */}
              {form.reviewType === 'course' && (
                <div>
                  <Label>Select Course</Label>
                  <select
                    className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-white"
                    value={form.course}
                    onChange={e => setForm(p => ({ ...p, course: e.target.value }))}
                    data-testid="review-course-select"
                  >
                    <option value="">Choose a course...</option>
                    {courses.map(c => (
                      <option key={c.id} value={c.id}>{c.title}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Rating */}
              <div>
                <Label>Rating *</Label>
                <div className="mt-1">
                  <StarRating rating={form.rating} onRate={r => setForm(p => ({ ...p, rating: r }))} />
                </div>
              </div>

              {/* Title */}
              <div>
                <Label>Title (optional)</Label>
                <Input
                  placeholder="Summarize your experience..."
                  value={form.title}
                  onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  data-testid="review-title-input"
                />
              </div>

              {/* Comment */}
              <div>
                <Label>Your Review *</Label>
                <Textarea
                  placeholder="Tell us about your experience..."
                  rows={4}
                  value={form.comment}
                  onChange={e => setForm(p => ({ ...p, comment: e.target.value }))}
                  data-testid="review-comment-input"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-rsc-yellow text-rsc-navy font-bold hover:bg-rsc-yellow/90"
                  data-testid="submit-review-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Submit Review
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Existing Reviews */}
      {reviews.length === 0 ? (
        <Card className="border-dashed border-2" data-testid="no-reviews">
          <CardContent className="p-12 text-center">
            <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-semibold text-gray-700 mb-2">No Reviews Yet</h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Be the first to share your experience with Ready Set STEM! Your feedback helps other families make informed decisions.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="reviews-list">
          {reviews.map((review) => (
            <Card key={review.id} className="hover:shadow-md transition-shadow" data-testid={`review-${review.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <StarRating rating={review.rating} size="sm" interactive={false} />
                      {review.is_featured && (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Featured</Badge>
                      )}
                    </div>
                    {review.title && <h4 className="font-semibold text-gray-900">{review.title}</h4>}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    {review.is_approved ? (
                      <><CheckCircle className="h-3.5 w-3.5 text-green-500" /> Approved</>
                    ) : (
                      <><Clock className="h-3.5 w-3.5 text-amber-500" /> Pending</>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-2">{review.comment}</p>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span>{review.user_name}</span>
                  <span>{new Date(review.created_at).toLocaleDateString()}</span>
                </div>
                {review.response && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-xs font-medium text-blue-700 mb-1">Admin Response:</p>
                    <p className="text-sm text-blue-600">{review.response}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
